import React from "react";
import { Award, Wallet, Gift, Coins, PartyPopper, Ticket } from "lucide-react";

const REWARDS = [
  {
    icon: Award,
    emoji: "\u{1F396}",
    title: "Founders Edition Member Card",
    subtitle: null,
    description: "Limited-release access pass with exclusive perks across the bHyyve network.",
    badge: null,
  },
  {
    icon: Wallet,
    emoji: "\u{1F4B0}",
    title: "$100 bHyyve Wallet Credit",
    subtitle: null,
    description: "Start spending immediately at launch — on us. Yours to use, no strings.",
    badge: "First 500 Only",
  },
  {
    icon: Gift,
    emoji: "\u{1F381}",
    title: "Exclusive Launch-Day Gift Box",
    subtitle: null,
    description: "Curated items and partner offers, delivered to celebrate day one.",
    badge: "First 500 Only",
  },
  {
    icon: Coins,
    emoji: "\u{1F48E}",
    title: "Service Credit Multipliers",
    subtitle: "Earn more when you use bHyyve.",
    description: "Triple your cashback rewards. Instead of 2% added back to your bHyyve wallet, enjoy 6 service credit multipliers that boost your cashback to 6% on any product or service — 3x cashback on your first 6 orders.",
    badge: null,
  },
  {
    icon: PartyPopper,
    emoji: "\u2728",
    title: "Exclusive Surprise Drops",
    subtitle: "Unexpected rewards, directly from bHyyve.",
    description: "Receive occasional, exclusive surprise drops to your email — reserved for Founding Members. No triggers, no hoops. Just added value, delivered at the right moment.",
    badge: null,
  },
  {
    icon: Ticket,
    emoji: "\u{1F39F}",
    title: "Double the Tickets for Community Giveaways",
    subtitle: "Win real products and services, just for being part of bHyyve.",
    description: "Get 2x entries for ongoing draws for free services, product bundles, and curated experiences across the bHyyve network — for the first 8 draws.",
    badge: null,
  },
];

export default function Incentives() {
  return (
    <section data-testid="incentives-section" className="px-4 sm:px-6">
      <div className="relative max-w-7xl mx-auto bg-bhyyve-charcoal rounded-[2.5rem] sm:rounded-[3rem] overflow-hidden grain">
        {/* Background glows */}
        <div aria-hidden="true" className="absolute -top-32 -right-20 w-[400px] h-[400px] rounded-full bg-bhyyve-honey/[0.15] blur-3xl" />
        <div aria-hidden="true" className="absolute -bottom-32 -left-20 w-[400px] h-[400px] rounded-full bg-white/[0.04] blur-3xl" />

        <div className="relative px-8 sm:px-12 lg:px-20 py-24 lg:py-32">
          {/* Header */}
          <div className="max-w-3xl mb-16 lg:mb-24">
            <div className="text-sm tracking-wider uppercase font-semibold text-bhyyve-honey mb-5">Members Only</div>
            <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] font-medium text-white">
              Founding Members <span className="italic font-normal text-bhyyve-honey">get more.</span>
            </h2>
            <p className="mt-7 text-xl text-white/70 max-w-xl leading-relaxed">
              A one-time release of perks for the people who bet on bHyyve early. Once they're gone, they're gone.
            </p>

            {/* 500 callout */}
            <div data-testid="first-500-callout" className="mt-10 inline-flex items-start gap-4 rounded-2xl bg-bhyyve-honey/10 border border-bhyyve-honey/30 px-6 py-5 max-w-2xl">
              <span aria-hidden="true" className="font-heading font-bold text-bhyyve-honey text-3xl leading-none mt-0.5">500</span>
              <p className="text-base leading-relaxed text-white/85">
                <span className="font-semibold text-white">The first 500 sign-ups get every perk below.</span> After those spots are filled, the{" "}
                <span className="text-bhyyve-honey font-semibold">$100 Wallet Credit</span> and{" "}
                <span className="text-bhyyve-honey font-semibold">Launch-Day Gift Box</span> are off the table — but everyone else still receives the rest of the Founding Member rewards.
              </p>
            </div>
          </div>

          {/* Reward cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
            {REWARDS.map((reward, i) => (
              <div
                key={i}
                data-testid={`incentive-card-${i}`}
                className={`group relative bg-white/[0.08] hover:bg-white/[0.12] backdrop-blur-md border p-8 rounded-3xl transition-all duration-500 flex flex-col gap-5 hover:-translate-y-1 ${
                  reward.badge ? "border-bhyyve-honey/35 hover:border-bhyyve-honey/60" : "border-white/10 hover:border-white/25"
                }`}
              >
                {reward.badge && (
                  <span data-testid={`first-500-badge-${i}`} className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-bhyyve-honey text-bhyyve-charcoal text-[11px] font-bold tracking-[0.14em] uppercase shadow-md">
                    {reward.badge}
                  </span>
                )}
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center group-hover:bg-bhyyve-honey/25 transition-colors">
                    <reward.icon className="w-6 h-6 text-bhyyve-honey" strokeWidth={1.75} aria-hidden="true" />
                  </div>
                  <span className="text-4xl filter drop-shadow-md group-hover:scale-110 transition-transform duration-500" aria-hidden="true">
                    {reward.emoji}
                  </span>
                </div>
                <div>
                  <h3 className="font-heading text-xl sm:text-2xl font-medium text-white mb-2 tracking-tight">{reward.title}</h3>
                  {reward.subtitle && <p className="text-sm font-semibold text-bhyyve-honey mb-2">{reward.subtitle}</p>}
                  <p className="text-base leading-relaxed text-white/70">{reward.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
